/**
 * Editor Types
 *
 * Type definitions for the Markdown editor and AI assistance.
 */

// ===== Template Variables =====

export interface TemplateVariable {
  key: string;
  label: string;
  category: 'cliente' | 'offerta' | 'tenant' | 'blocchi';
  example?: string;
}

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  // Cliente variables
  { key: '{{cliente.nome}}', label: 'Nome Cliente', category: 'cliente', example: 'Mario Rossi' },
  { key: '{{cliente.ragione_sociale}}', label: 'Ragione Sociale', category: 'cliente', example: 'Rossi S.r.l.' },
  { key: '{{cliente.email}}', label: 'Email Cliente', category: 'cliente', example: 'mario@example.com' },
  { key: '{{cliente.telefono}}', label: 'Telefono', category: 'cliente', example: '+39 123 456 7890' },
  { key: '{{cliente.indirizzo}}', label: 'Indirizzo', category: 'cliente', example: 'Via Roma 1, Milano' },
  { key: '{{cliente.partita_iva}}', label: 'P.IVA', category: 'cliente', example: 'IT12345678901' },

  // Offerta variables
  { key: '{{offerta.numero}}', label: 'Numero Offerta', category: 'offerta', example: '2024-001' },
  { key: '{{offerta.titolo}}', label: 'Titolo', category: 'offerta', example: 'Proposta servizi web' },
  { key: '{{offerta.importo_totale}}', label: 'Importo Totale', category: 'offerta', example: '€1.500,00' },
  { key: '{{offerta.importo_imponibile}}', label: 'Imponibile', category: 'offerta', example: '€1.229,51' },
  { key: '{{offerta.importo_iva}}', label: 'IVA', category: 'offerta', example: '€270,49' },
  { key: '{{offerta.data_creazione}}', label: 'Data Creazione', category: 'offerta', example: '16/12/2024' },
  { key: '{{offerta.scadenza}}', label: 'Scadenza', category: 'offerta', example: '15/01/2025' },
  { key: '{{offerta.validita}}', label: 'Giorni Validità', category: 'offerta', example: '30 giorni' },

  // Tenant variables
  { key: '{{tenant.nome}}', label: 'Nome Azienda', category: 'tenant', example: 'La Mia Azienda' },
  { key: '{{tenant.ragione_sociale}}', label: 'Ragione Sociale Azienda', category: 'tenant', example: 'Azienda S.r.l.' },
  { key: '{{tenant.indirizzo}}', label: 'Indirizzo Azienda', category: 'tenant', example: 'Via Milano 10' },
  { key: '{{tenant.partita_iva}}', label: 'P.IVA Azienda', category: 'tenant', example: 'IT98765432101' },
  { key: '{{tenant.telefono}}', label: 'Telefono Azienda', category: 'tenant', example: '+39 02 1234567' },
  { key: '{{tenant.email}}', label: 'Email Azienda', category: 'tenant', example: 'info@azienda.it' },

  // Special blocks
  { key: '{{items_table}}', label: 'Tabella Articoli', category: 'blocchi', example: '[Tabella]' },
  { key: '{{totals}}', label: 'Riepilogo Totali', category: 'blocchi', example: '[Totali]' },
  { key: '{{signature_block}}', label: 'Blocco Firma', category: 'blocchi', example: '[Firma]' },
  { key: '{{cliente_info}}', label: 'Info Cliente Completo', category: 'blocchi', example: '[Info Cliente]' },
];


// ===== AI Types =====

export type AIOperationType = 'suggestion' | 'generation' | 'improvement' | 'expansion';

export type ImprovementType = 'general' | 'clarity' | 'persuasion' | 'grammar' | 'formal' | 'concise';

export type ExpansionLength = 'short' | 'medium' | 'long';

export interface AISuggestRequest {
  current_text: string;
  context?: string;
  field_name?: string;
  offerta_id?: string;
}

export interface AIGenerateRequest {
  section_type: string;
  context: Record<string, unknown>;
  custom_instructions?: string;
  offerta_id?: string;
  stream?: boolean;
}

export interface AIImproveRequest {
  text: string;
  improvement_type: ImprovementType;
  context?: string;
  offerta_id?: string;
}

export interface AIExpandRequest {
  text: string;
  target_length: ExpansionLength;
  context?: string;
  offerta_id?: string;
}

export interface AIResponse {
  content: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  model: string;
  provider: string;
  latency_ms: number;
}

export interface AIStreamChunk {
  content: string;
  is_final: boolean;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  error?: string;
}

export interface AIConfig {
  ai_enabled: boolean;
  ai_suggestions_enabled: boolean;
  ai_generation_enabled: boolean;
  ai_improvement_enabled: boolean;
  provider: string;
  model: string;
}

export interface AIUsageStats {
  requests_this_hour: number;
  tokens_this_month: number;
  hour: string;
  month: string;
  limits?: {
    max_requests_per_hour: number;
    max_tokens_per_month: number;
  };
  tokens_remaining_this_month?: number | null;
  requests_remaining_this_hour?: number | null;
}

export type AIProvider = 'openai' | 'anthropic' | 'ollama' | 'openrouter';

export interface AIUsageHistoryMonth {
  month: string;
  total_tokens: number;
  requests: number;
  success_count: number;
  operations: Record<string, number>;
}

export interface AIUsageHistoryResponse {
  months: AIUsageHistoryMonth[];
}


// ===== Editor Types =====

export interface EditorState {
  content: string;
  cursorPosition: number;
  selection: { from: number; to: number } | null;
  isDirty: boolean;
  lastSaved: Date | null;
}

export interface EditorToolbarAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
  action: () => void;
  disabled?: boolean;
}

export interface SectionType {
  id: string;
  label: string;
  description: string;
  defaultPrompt?: string;
}

export const SECTION_TYPES: SectionType[] = [
  {
    id: 'introduzione',
    label: 'Introduzione',
    description: 'Testo di apertura e saluto al cliente',
    defaultPrompt: 'Crea un\'introduzione cordiale e professionale'
  },
  {
    id: 'descrizione_servizi',
    label: 'Descrizione Servizi',
    description: 'Descrizione dettagliata dei servizi offerti',
    defaultPrompt: 'Descrivi i servizi in modo chiaro e convincente'
  },
  {
    id: 'benefici',
    label: 'Benefici',
    description: 'Vantaggi e valore per il cliente',
    defaultPrompt: 'Evidenzia i benefici principali per il cliente'
  },
  {
    id: 'metodologia',
    label: 'Metodologia',
    description: 'Approccio e processo di lavoro',
    defaultPrompt: 'Spiega il nostro approccio e metodologia'
  },
  {
    id: 'tempistiche',
    label: 'Tempistiche',
    description: 'Timeline e milestone del progetto',
    defaultPrompt: 'Definisci le tempistiche del progetto'
  },
  {
    id: 'condizioni',
    label: 'Condizioni',
    description: 'Termini e condizioni contrattuali',
    defaultPrompt: 'Specifica le condizioni commerciali'
  },
  {
    id: 'conclusione',
    label: 'Conclusione',
    description: 'Chiusura e call-to-action',
    defaultPrompt: 'Crea una conclusione convincente con call-to-action'
  },
];


// ===== Context for Variable Interpolation =====

export interface OfferContext {
  cliente: {
    nome?: string;
    ragione_sociale?: string;
    email?: string;
    telefono?: string;
    indirizzo?: string;
    partita_iva?: string;
  };
  offerta: {
    numero?: string;
    titolo?: string;
    importo_totale?: number;
    importo_imponibile?: number;
    importo_iva?: number;
    data_creazione?: string;
    scadenza?: string;
    validita?: number;
  };
  tenant: {
    nome?: string;
    ragione_sociale?: string;
    indirizzo?: string;
    partita_iva?: string;
    telefono?: string;
    email?: string;
  };
  items?: Array<{
    descrizione: string;
    quantita: number;
    prezzo_unitario: number;
    importo_totale: number;
  }>;
}


// ===== Utility Functions =====

/**
 * Format a number as Italian currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
}

/**
 * Format a date in Italian format
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('it-IT').format(new Date(date));
}

/**
 * Interpolate variables in markdown content
 */
export function interpolateVariables(content: string, context: OfferContext): string {
  let result = content;

  // Cliente variables
  if (context.cliente) {
    result = result.replace(/\{\{cliente\.nome\}\}/g, context.cliente.nome || '');
    result = result.replace(/\{\{cliente\.ragione_sociale\}\}/g, context.cliente.ragione_sociale || '');
    result = result.replace(/\{\{cliente\.email\}\}/g, context.cliente.email || '');
    result = result.replace(/\{\{cliente\.telefono\}\}/g, context.cliente.telefono || '');
    result = result.replace(/\{\{cliente\.indirizzo\}\}/g, context.cliente.indirizzo || '');
    result = result.replace(/\{\{cliente\.partita_iva\}\}/g, context.cliente.partita_iva || '');
  }

  // Offerta variables
  if (context.offerta) {
    result = result.replace(/\{\{offerta\.numero\}\}/g, context.offerta.numero || '');
    result = result.replace(/\{\{offerta\.titolo\}\}/g, context.offerta.titolo || '');
    result = result.replace(/\{\{offerta\.importo_totale\}\}/g,
      context.offerta.importo_totale ? formatCurrency(context.offerta.importo_totale) : '');
    result = result.replace(/\{\{offerta\.importo_imponibile\}\}/g,
      context.offerta.importo_imponibile ? formatCurrency(context.offerta.importo_imponibile) : '');
    result = result.replace(/\{\{offerta\.importo_iva\}\}/g,
      context.offerta.importo_iva ? formatCurrency(context.offerta.importo_iva) : '');
    result = result.replace(/\{\{offerta\.data_creazione\}\}/g,
      context.offerta.data_creazione ? formatDate(context.offerta.data_creazione) : '');
    result = result.replace(/\{\{offerta\.scadenza\}\}/g,
      context.offerta.scadenza ? formatDate(context.offerta.scadenza) : '');
    result = result.replace(/\{\{offerta\.validita\}\}/g,
      context.offerta.validita ? `${context.offerta.validita} giorni` : '');
  }

  // Tenant variables
  if (context.tenant) {
    result = result.replace(/\{\{tenant\.nome\}\}/g, context.tenant.nome || '');
    result = result.replace(/\{\{tenant\.ragione_sociale\}\}/g, context.tenant.ragione_sociale || '');
    result = result.replace(/\{\{tenant\.indirizzo\}\}/g, context.tenant.indirizzo || '');
    result = result.replace(/\{\{tenant\.partita_iva\}\}/g, context.tenant.partita_iva || '');
    result = result.replace(/\{\{tenant\.telefono\}\}/g, context.tenant.telefono || '');
    result = result.replace(/\{\{tenant\.email\}\}/g, context.tenant.email || '');
  }

  return result;
}
